import PropTypes from "prop-types";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Influencer } from "../../domain";
import { useCurrentUser } from "../../globalState";
import useQuery from "../../hooks/useQuery";
import useShopInfo from "../../hooks/useShopInfo";
import useShopSplit from "../../hooks/useShopSplit";
import { Badge, Button, InfoIcon } from "../../themeV2";
import { attention } from "./attention";
import { shopIdentity, visibility } from "./shop";
import css from "./ShopInfo.module.css";
import ShopInfoDrawer from "./ShopInfoDrawer";
import useOrderPipeline from "./useOrderPipeline";
import { sellsOwnProducts } from "./utils";

const OPEN_PARAM = "shopInfo";

const readIsOpen = (params) => params.has(OPEN_PARAM);

const useOpenState = () => {
  const history = useHistory();
  const location = useLocation();

  const isOpen = useQuery(readIsOpen);

  const setOpen = React.useCallback(
    (next) => {
      const params = new URLSearchParams(location.search);

      if (next) {
        params.set(OPEN_PARAM, "1");
      } else {
        params.delete(OPEN_PARAM);
      }

      const search = params.toString();

      history.replace(`${location.pathname}${search ? `?${search}` : ""}`);
    },
    [history, location.pathname, location.search],
  );

  return [isOpen, setOpen];
};

/*
 * Admin-only operational summary for the shop currently in view. The badge
 * gives the at-a-glance signal without an interaction; the drawer holds the
 * detail and links out to the surfaces that own the actions.
 */
export default function ShopInfo({ activeInfluencer }) {
  const currentUser = useCurrentUser();
  const [isOpen, setOpen] = useOpenState();

  const influencerId = activeInfluencer?.influencerId;
  const { shopInfo, isLoading, isError, refresh } = useShopInfo(
    influencerId,
    currentUser.isAdmin,
  );

  // Identity, visibility and the order pipeline all come from data the dashboard
  // already holds. They are merged onto the fetched payload here so the drawer
  // stays a view over a single object, with one shape whatever its parts cost.
  const orders = useOrderPipeline({
    route: activeInfluencer?.route,
    influencerId,
    enabled: currentUser.isAdmin && sellsOwnProducts(activeInfluencer),
  });

  // Who the shop currently pays comes from the endpoint the Shop Split editor
  // already uses, so the drawer and that editor cannot disagree about it.
  const { shopSplit } = useShopSplit(currentUser.isAdmin ? influencerId : null);

  const payload = React.useMemo(
    () =>
      shopInfo && activeInfluencer
        ? {
            ...shopInfo,
            shop: shopIdentity(activeInfluencer),
            visibility: visibility(activeInfluencer),
            orders,
            payout: { ...shopInfo.payout, shopSplit },
            attention: attention({ orders, ...shopInfo }),
          }
        : null,
    [shopInfo, activeInfluencer, orders, shopSplit],
  );

  const handleOpen = React.useCallback(() => setOpen(true), [setOpen]);
  const handleClose = React.useCallback(() => setOpen(false), [setOpen]);

  if (!currentUser.isAdmin || !influencerId) {
    return null;
  }

  const attentionCount = payload?.attention?.count ?? 0;
  const attentionLabel =
    attentionCount > 0
      ? `Shop info, ${attentionCount} ${
          attentionCount === 1 ? "item needs" : "items need"
        } attention`
      : "Shop info";

  return (
    <React.Fragment>
      <Button
        variant="text"
        size="sm"
        fullWidth
        onClick={handleOpen}
        leading={<InfoIcon />}
        className={css.trigger}
        aria-label={attentionLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        Shop info
        {attentionCount > 0 ? (
          <span className={css.triggerBadge} aria-hidden="true">
            <Badge variant="count" count={attentionCount} />
          </span>
        ) : null}
      </Button>

      <ShopInfoDrawer
        open={isOpen}
        onClose={handleClose}
        shopInfo={payload}
        isLoading={isLoading}
        isError={isError}
        onRetry={refresh}
      />
    </React.Fragment>
  );
}

ShopInfo.propTypes = {
  activeInfluencer: PropTypes.instanceOf(Influencer),
};
